import { format, formatDistanceToNow, isToday, isYesterday, isThisYear, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

// Create a global timezone variable that can be accessed without hooks
// This will be updated by the TimezoneProvider when it mounts
declare global {
  interface Window {
    __CRIMSON_TIMEZONE__?: string;
  }
}

/**
 * Returns user's local timezone or default timezone
 */
export function getUserTimezone(): string {
  if (typeof window !== 'undefined') {
    // Check for the global timezone variable first
    if (window.__CRIMSON_TIMEZONE__) {
      return window.__CRIMSON_TIMEZONE__;
    }
    
    try {
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      // Set the global timezone variable for future use
      window.__CRIMSON_TIMEZONE__ = browserTimezone;
      return browserTimezone;
    } catch (e) {
      console.error('Error getting browser timezone:', e);
    }
  }
  
  // Default to UTC (especially for server-side rendering)
  return 'UTC';
}

/**
 * Format a date from ISO string or Date object to a localized date string
 * @param date Date to format (ISO string or Date object)
 * @param formatStr Format string for date-fns (defaults to 'd MMM yyyy')
 * @param timezone Timezone to use (defaults to user's local timezone)
 * @returns Formatted date string
 */
export function formatDate(
  date: string | Date | null | undefined,
  formatStr: string = 'd MMM yyyy',
  timezone?: string
): string {
  if (!date) return '-';
  
  try {
    if (typeof date === 'string') {
      // Check if string is in ISO format (YYYY-MM-DD)
      const isISODateString = /^\d{4}-\d{2}-\d{2}/.test(date);
      if (isISODateString) {
        const parsedDate = parseISO(date);
        if (timezone) {
          const tz = timezone || getUserTimezone();
          const zonedDate = toZonedTime(parsedDate, tz);
          return format(zonedDate, formatStr);
        }
        return format(parsedDate, formatStr);
      }
      // If not in ISO format, return the string as is
      return format(date, formatStr);
    }
    
    if (isNaN(date.getTime())) return '-';

    if (timezone) {
      const tz = timezone || getUserTimezone();
      const zonedDate = toZonedTime(date, tz);
      return format(zonedDate, formatStr);
    }

    return format(date, formatStr);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '-';
  }
}

export type RegionalDateLocale = 'uk' | 'us';

/** US calendars start on Sunday; UK (and other) calendars start on Monday. */
export function weekStartsOnForLocale(locale: RegionalDateLocale): 0 | 1 {
  return locale === 'us' ? 0 : 1;
}


function regionalDateFormat(
  locale: RegionalDateLocale = 'uk',
  options: { month?: 'short' | 'long'; includeTime?: boolean; includeYear?: boolean } = {},
): string {
  const month = options.month === 'long' ? 'MMMM' : 'MMM';
  const includeYear = options.includeYear !== false;
  const datePart = locale === 'us'
    ? `${month} d${includeYear ? ', yyyy' : ''}`
    : `d ${month}${includeYear ? ' yyyy' : ''}`;
  return options.includeTime ? `${datePart}, HH:mm` : datePart;
}

export function formatRegionalDate(
  date: string | Date | null | undefined,
  locale: RegionalDateLocale = 'uk',
  options?: { month?: 'short' | 'long'; includeTime?: boolean; includeYear?: boolean },
): string {
  return formatDate(date, regionalDateFormat(locale, options));
}

/**
 * Format a date with smart formatting based on how recent it is:
 * - Today: "Today, 3:45 PM"
 * - Yesterday: "Yesterday, 3:45 PM"
 * - This year: "May 5, 3:45 PM"
 * - Earlier: "May 5, 2024, 3:45 PM"
 * @param date Date to format (ISO string or Date object)
 * @param timezone Timezone to use (defaults to user's local timezone)
 * @returns Smartly formatted date string
 */
export function formatSmartDate(
  date: string | Date | null | undefined,
  timezone?: string
): string {
  if (!date) return '-';
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (isNaN(dateObj.getTime())) return '-';
    
    const tz = timezone || getUserTimezone();
    const zonedDate = toZonedTime(dateObj, tz);
    
    if (isToday(zonedDate)) {
      return `Today, ${format(zonedDate, 'h:mm a')}`;
    } else if (isYesterday(zonedDate)) {
      return `Yesterday, ${format(zonedDate, 'h:mm a')}`;
    } else if (isThisYear(zonedDate)) {
      return format(zonedDate, 'MMM d, h:mm a');
    } else {
      return format(zonedDate, 'MMM d, yyyy, h:mm a');
    }
  } catch (error) {
    console.error('Error formatting smart date:', error);
    return '-';
  }
}

/**
 * Format a date relative to now (e.g. "2 days ago", "5 minutes ago")
 * @param date Date to format (ISO string or Date object)
 * @param addSuffix Whether to add the suffix (defaults to true)
 * @returns Formatted relative date string
 */
export function formatRelativeTime(
  date: string | Date | null | undefined,
  addSuffix: boolean = true
): string {
  if (!date) return '-';
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (isNaN(dateObj.getTime())) return '-';
    
    return formatDistanceToNow(dateObj, { addSuffix });
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return '-';
  }
}

/**
 * Formats how long a piece of work took, for a chip the lawyer reads at a
 * glance. Rounds up so a turn is never reported as having taken no time.
 * @param durationMs Elapsed milliseconds
 * @returns Coarse duration such as "45s", "2m 10s", or "1h 5m"
 */
export function formatElapsedDuration(durationMs: number): string {
  const totalSeconds = Math.max(1, Math.ceil(durationMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

/**
 * Converts a UTC ISO string to a Date object in the user's timezone
 * @param isoString ISO string to convert
 * @param timezone Timezone to use (defaults to user's local timezone)
 * @returns Date object in the user's timezone
 */
export function isoToZonedDate(
  isoString: string | null | undefined,
  timezone?: string
): Date | null {
  if (!isoString) return null;
  
  try {
    const dateObj = parseISO(isoString);
    if (isNaN(dateObj.getTime())) return null;
    
    const tz = timezone || getUserTimezone();
    return toZonedTime(dateObj, tz);
  } catch (error) {
    console.error('Error converting ISO to zoned date:', error);
    return null;
  }
}
